<template>
    <div class="alarm-type-options">
        <button class="select-all-row" type="button" @click="toggleAll">
            <span>{{ $t( 'selectAll' ) }}</span>
            <reo-checkbox
                :modelValue="allState.checked"
                :indeterminate="allState.indeterminate"
                :isRound="false"
                @change="toggleAll"
            />
        </button>

        <div class="option-divider"></div>

        <div class="option-list">
            <section
                v-for="option in options"
                :key="option.id"
                class="option-section"
            >
                <button class="option-row" type="button" @click="toggleOption( option )">
                    <span class="option-label">
                        <MotionImg
                            v-if="option.iconUrl"
                            :src="option.iconUrl"
                            class="option-icon"
                        />
                        <span>{{ option.label }}</span>
                    </span>
                    <reo-checkbox
                        :modelValue="selectionState( option ).checked"
                        :indeterminate="selectionState( option ).indeterminate"
                        :isRound="false"
                        @change="toggleOption( option )"
                    />
                </button>

                <div v-if="option.children.length > 0" class="subdivision">
                    <button
                        class="subdivision-header"
                        type="button"
                        @click="toggleExpanded( option.id )"
                    >
                        <span>{{ $t( 'cloud.alarmTypeSelector.subdivisionLabel' ) }}</span>
                        <span class="subdivision-summary">
                            {{ selectedChildCount( option ) }}/{{ option.children.length }}
                            <i
                                class="icon-co_down subdivision-arrow"
                                :class="{ expanded: isExpanded( option.id ) }"
                            ></i>
                        </span>
                    </button>

                    <template v-if="isExpanded( option.id )">
                        <button
                            v-for="child in option.children"
                            :key="child.id"
                            class="child-row"
                            type="button"
                            @click="toggleOption( child )"
                        >
                            <span>{{ child.label }}</span>
                            <reo-checkbox
                                :modelValue="selectedKeys.includes( child.key )"
                                :isRound="false"
                                @change="toggleOption( child )"
                            />
                        </button>
                    </template>
                </div>
            </section>
        </div>
    </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import ReoCheckbox from '@/components/reoCheckbox.vue';
import MotionImg from '@/components/MotionImg.vue';
import {
    getAlarmTypeSelectionState,
    getAllAlarmTypeSelectionState,
    getSelectableAlarmTypeKeys,
    IAlarmTypeTreeOption
} from '../../lib/alarmTypeSelector';

export default Vue.extend({
    name: 'AlarmTypeOptions',
    components: {
        ReoCheckbox,
        MotionImg
    },
    props: {
        options: {
            type: Array as PropType<IAlarmTypeTreeOption[]>,
            required: true
        },
        selectedKeys: {
            type: Array as PropType<string[]>,
            required: true
        }
    },
    data() {

        return {
            expandedIds: [] as number[]
        };

    },
    computed: {
        allState() {

            return getAllAlarmTypeSelectionState( this.options, this.selectedKeys );

        }
    },
    methods: {
        selectionState( option: IAlarmTypeTreeOption ) {

            return getAlarmTypeSelectionState( option, this.selectedKeys );

        },
        selectedChildCount( option: IAlarmTypeTreeOption ): number {

            return option.children.filter( child => this.selectedKeys.includes( child.key )).length;

        },
        isExpanded( id: number ): boolean {

            return this.expandedIds.includes( id );

        },
        toggleExpanded( id: number ): void {

            if ( this.isExpanded( id )) {

                this.expandedIds = this.expandedIds.filter( expandedId => expandedId !== id );

            } else {

                this.expandedIds = [...this.expandedIds, id];

            }

            this.$nextTick(() => this.$emit( 'layout-change' ));

        },
        toggleAll(): void {

            this.$emit(
                'change',
                this.allState.checked ? [] : getSelectableAlarmTypeKeys( this.options )
            );

        },
        toggleOption( option: IAlarmTypeTreeOption ): void {

            const optionKeys = option.children.length > 0
                ? option.children.map( child => child.key )
                : [option.key];
            const state = getAlarmTypeSelectionState( option, this.selectedKeys );
            const selectedSet = new Set( this.selectedKeys );

            for ( const key of optionKeys ) {

                if ( state.checked ) selectedSet.delete( key );
                else selectedSet.add( key );

            }

            this.$emit( 'change', Array.from( selectedSet ));

        }
    }
});
</script>

<style lang="less" scoped>
@import (reference) '@reolink-web/components.design-tokens/less';

.alarm-type-options {
    color: @font_primary;
    font-family: @font_01;

    /deep/ .ensure-box[checkbox] .check-icon.icon-checked,
    /deep/ .ensure-box[checkbox] .check-icon.icon-indeterminate {
        background-color: @b_6;
    }
}

button {
    width: 100%;
    padding: 0;
    border: 0;
    color: inherit;
    font: inherit;
    text-align: left;
    background: transparent;
    cursor: pointer;
}

.select-all-row,
.option-row,
.child-row,
.subdivision-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;
}

.select-all-row {
    .body_4_400();
    min-height: 28px;
    color: @b_6;
}

.option-divider {
    height: 1px;
    margin-top: @spacing_md;
    background: @line;
}

.option-section + .option-section {
    border-top: 1px solid @line;
}

.option-row {
    .body_4_400();
    min-height: 46px;
}

.option-label {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: @spacing_md;
}

.option-icon {
    flex: 0 0 22px;
}

.subdivision {
    padding: 0 @spacing_xl;
    margin-bottom: @spacing_xl;
    overflow: hidden;
    border-radius: @radius_xs;
    background: @bg_02;
}

.subdivision-header,
.child-row {
    .body_4_400();
    min-height: 46px;
}

.child-row {
    border-top: 1px solid @line;
}

.subdivision-summary {
    .label_1_400();
    display: inline-flex;
    align-items: center;
    gap: @spacing_xs;
}

.subdivision-arrow {
    display: inline-flex;
    width: 20px;
    height: 20px;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    transition: transform .2s ease;

    &.expanded {
        transform: rotate(180deg);
    }
}

@media screen and (max-width: 1199px) {
    .select-all-row {
        .body_3_400();
        min-height: 44px;
        padding: 0 @spacing_2xl;
    }

    .option-divider {
        margin: 0 @spacing_2xl;
    }

    .option-list {
        padding: 0 @spacing_2xl;
        margin-top: @spacing_2xl;
        overflow: hidden;
        border-radius: @radius_md;
        background: @bg_01;
    }

    .option-section {
        padding: 0;
        margin: 0;
        border-radius: 0;
        background: transparent;
    }

    .option-section + .option-section {
        border-top: 1px solid @line;
    }

    .option-row {
        .body_3_400();
        min-height: 56px;
    }

    .subdivision {
        margin-bottom: @spacing_2xl;
    }

    .subdivision-header,
    .child-row {
        .body_3_400();
        min-height: 48px;
    }

    .subdivision-summary {
        .body_4_400();
    }
}
</style>
